import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Download, Loader2, Menu, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import RunCompsetModal from '../modals/RunCompsetModal';
import SelectCompset from '../select/SelectCompset';
import { useCompset } from '@/contexts/CompsetContext';
import { storeActiveCompset } from '@/helpers/localStorageHandlers';
import FilterBar from './FilterBar';
import { exportJsonToExcel } from '@/helpers/exportJsonToExcel';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';
import ExecutingCompsetModal from '../modals/ExecutingCompsetModal';
import { useMutation } from '@tanstack/react-query';
import { reportServices } from '@/services/dashboard.service';
import { queryKeys } from '@/services/queryKeyFactory';
import { useMediaQuery } from '@/hooks/useMediaQuery';

export default function DashboardHeader({ onToggleSidebar }) {
  const [runCompsetModalStatus, setRunCompsetModalStatus] = useState(false);
  const [executingCompsetModalStatus, setExecutingCompsetModalStatus] = useState(false);
  const [compsetExecutionStep, setCompsetExecutionStep] = useState(0);

  const { compsets, isErrorCompsets, isLoadingCompsets, activeCompset, setActiveCompset } = useCompset();

  const isMobile = useMediaQuery('(max-width: 767px)');

  const handleRunCompSet = () => setRunCompsetModalStatus(true);

  const { mutate: exportMutation, isPending: isExportingMarketAnalyticsData } = useMutation({
    mutationKey: queryKeys.exportAnalyticsData(),
    mutationFn: () => reportServices.exportAnalysisData(activeCompset?.compset_id),
    onMutate: () => {
      toast.success('Export started', {
        description: 'Your analytics data is being prepared...',
      });
    },
    onError: (error) => apiErrorHandler(error),
    onSuccess: (data) => {
      if (!data?.data?.length) {
        toast.error('Export unavailable: Dataset is empty.');
      } else {
        exportJsonToExcel(data?.data);
      }
    },
  });

  const onCompsetChange = (compsetId) => {
    const compset = compsets?.user_compsets?.find((comp) => comp?.compset_id == compsetId);
    if (compset) {
      storeActiveCompset(JSON.stringify(compset));
      setActiveCompset(compset);
    }
  };

  const hasCompsets = !!compsets?.user_compsets;
  const isReady = !isLoadingCompsets && compsets && activeCompset && !isErrorCompsets;

  return (
    <header className="sticky top-0 z-30 bg-card/95 backdrop-blur-md border-b border-border/60">
      {/* Accent line */}
      <div className="h-[2px] w-full bg-gradient-to-r from-primary/40 via-primary to-primary/40" />

      <div className="px-4 sm:px-6 py-3 space-y-2">

        {/* ── ROW 1 ──────────────────────────────────────────────────────────
            Mobile:  [☰] [CompsetSelect] [RunCount] [Run]
            Desktop: [CompsetSelect] [RunCount] [Run]  ──────  [FilterBar] [Export]
        ────────────────────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-2">

          {/* Hamburger – mobile only */}
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-xl hover:bg-primary/10 hover:text-primary transition-colors"
              onClick={onToggleSidebar}
              aria-label="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}

          {/* Compset selector */}
          {isReady && (
            <SelectCompset
              value={activeCompset?.compset_id}
              onChange={onCompsetChange}
              activeIndicator={true}
              classNames="w-36 sm:w-48"
            />
          )}

          {/* Run Count pill */}
          {hasCompsets && activeCompset && (
            <div className="flex items-center gap-1.5 h-9 px-3 rounded-xl border border-border bg-muted/40 text-sm shrink-0">
              <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground hidden sm:inline">Runs left:</span>
              <span className="font-semibold tabular-nums text-foreground">
                {activeCompset?.refresh_remain}
              </span>
            </div>
          )}

          {/* Run CompSet */}
          {hasCompsets && (
            <Button
              onClick={handleRunCompSet}
              size="sm"
              className="gap-2 h-9 rounded-xl bg-primary hover:bg-primary/90 shadow-sm shadow-primary/20 font-medium shrink-0"
            >
              <Play className="h-3.5 w-3.5 fill-current" />
              <span className="hidden sm:inline">Run CompSet</span>
              <span className="sm:hidden">Run</span>
            </Button>
          )}

          {/* Spacer – pushes FilterBar + Export to the right on desktop */}
          <div className="flex-1" />

          {/* FilterBar – desktop only (inline in row 1) */}
          <div className="hidden md:flex">
            <FilterBar />
          </div>

          {/* Export – desktop only (inline in row 1) */}
          <div className="hidden md:flex">
            <Button
              variant="outline"
              size="sm"
              onClick={exportMutation}
              disabled={isExportingMarketAnalyticsData}
              className="gap-2 h-9 rounded-xl border-border/80 hover:border-primary/40 hover:bg-primary/5 hover:text-primary transition-all font-medium"
            >
              {isExportingMarketAnalyticsData ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Exporting…
                </>
              ) : (
                <>
                  <Download className="h-3.5 w-3.5" />
                  Export
                </>
              )}
            </Button>
          </div>
        </div>

        {/* ── ROW 2 (mobile only) ─────────────────────────────────────────────
            [FilterBar  ←  flex-1  →  ]  [Export]
        ────────────────────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-2 md:hidden">
          {/* FilterBar stretches to fill available space */}
          <div className="flex-1 min-w-0">
            <FilterBar />
          </div>

          {/* Export */}
          <Button
            variant="outline"
            size="sm"
            onClick={exportMutation}
            disabled={isExportingMarketAnalyticsData}
            className="gap-2 h-9 rounded-xl border-border/80 hover:border-primary/40 hover:bg-primary/5 hover:text-primary transition-all font-medium shrink-0"
          >
            {isExportingMarketAnalyticsData ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span className="hidden sm:inline">Exporting…</span>
              </>
            ) : (
              <>
                <Download className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Export</span>
              </>
            )}
          </Button>
        </div>

      </div>

      {/* Modals — unchanged */}
      <RunCompsetModal
        open={runCompsetModalStatus}
        onClose={() => setRunCompsetModalStatus(false)}
        loading={executingCompsetModalStatus}
        onLoadingChange={setExecutingCompsetModalStatus}
        updateStep={setCompsetExecutionStep}
      />
      <ExecutingCompsetModal
        isExecuting={executingCompsetModalStatus}
        step={compsetExecutionStep}
      />
    </header>
  );
}